import { ref } from 'vue'
import Network from '@/Network'
import type { AddressResponse } from '@/Network/Services/ConsultingAddressService'
import { getApiErrorMessage } from '@/utils/apiError'

/**
 * Fallback do aviso quando a falha não traz mensagem própria (erro cru, bug).
 * O service já distingue "CEP não encontrado" de "serviço indisponível".
 */
export const CEP_LOOKUP_WARNING_FALLBACK =
  'Não foi possível buscar o endereço. Preencha manualmente.'

/**
 * Busca de endereço por CEP (ViaCEP via backend).
 *
 * Falha é degradável: `lookup` devolve `null` e o formulário segue no
 * preenchimento manual. O motivo fica em `lookupWarning` para virar um aviso
 * junto do campo — não um erro bloqueante nem um toast, porque o cadastro
 * continua possível sem o CEP.
 */
export function useCepLookup() {
  const isLoadingCep = ref(false)
  const lookupWarning = ref<string | null>(null)

  function clearWarning(): void {
    lookupWarning.value = null
  }

  async function lookup(cep: string): Promise<AddressResponse | null> {
    // Cada nova digitação descarta o aviso do CEP anterior.
    clearWarning()
    const rawCep = cep.replace(/\D/g, '')
    if (rawCep.length !== 8) return null

    isLoadingCep.value = true
    try {
      return await Network.consultingAddress.getAddressByCep(rawCep)
    } catch (error) {
      console.warn('Busca de CEP falhou, permitindo preenchimento manual.', error)
      lookupWarning.value = getApiErrorMessage(error, CEP_LOOKUP_WARNING_FALLBACK)
      return null
    } finally {
      isLoadingCep.value = false
    }
  }

  return { isLoadingCep, lookupWarning, lookup, clearWarning }
}
